import React, { useState, useEffect } from 'react';
import { researchAPI } from '../services/api';
import { useToast } from './Toast';
import { formatLabel, normalizeDocumentFormat } from '../constants/documentFormat';
import './ResearchReports.css';

function formatAngleScore(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

function angleScoreBand(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'none';
  if (n >= 8) return 'high';
  if (n >= 5) return 'mid';
  return 'low';
}

function AngleScoreCell({ value, label }) {
  const band = angleScoreBand(value);
  return (
    <span
      className={`rr-score-cell rr-score-cell--${band}`}
      title={label}
      aria-label={`${label}: ${formatAngleScore(value)}`}
    >
      {formatAngleScore(value)}
    </span>
  );
}

/**
 * Single-report workspace: diagnostics, angles, approve/reject, modals.
 * Used by the research report detail route.
 */
function ResearchReportBody({ report, profiles, onRefresh }) {
  const { showToast } = useToast();
  const reportId = report.id;

  const [selectedIdeas, setSelectedIdeas] = useState({});
  const [rejectModal, setRejectModal] = useState(null);
  const [angleDetailModal, setAngleDetailModal] = useState(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  /** Standalone reports: optional topic profile or one-off queue (no saved profile) */
  const QUEUE_PROFILE_STANDALONE = 'standalone';
  const [queueProfileChoice, setQueueProfileChoice] = useState(() => ({}));

  useEffect(() => {
    const anyModal = rejectModal || angleDetailModal;
    if (!anyModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (rejectModal) setRejectModal(null);
        if (angleDetailModal) setAngleDetailModal(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [rejectModal, angleDetailModal]);

  const reload = async () => {
    if (onRefresh) await onRefresh();
  };

  const handleApproveIdeas = async (id, ideaIndices, rep) => {
    if (!ideaIndices || ideaIndices.length === 0) {
      showToast('Please select at least one angle to approve', 'warning');
      return;
    }

    const extra = {};
    if (rep.profile_id == null) {
      const chosen = queueProfileChoice[id] ?? QUEUE_PROFILE_STANDALONE;
      extra.profile_id =
        chosen === QUEUE_PROFILE_STANDALONE ? null : parseInt(chosen, 10);
    }

    try {
      await researchAPI.approveIdeas(id, ideaIndices, extra);
      showToast(`${ideaIndices.length} angle(s) approved and moved to draft candidates`, 'success');
      setSelectedIdeas((prev) => {
        const u = { ...prev };
        delete u[id];
        return u;
      });
      await reload();
    } catch (err) {
      showToast(
        `Failed to approve angles: ${err.response?.data?.error || err.message || 'Unknown error'}`,
        'error'
      );
      console.error(err);
    }
  };

  const toggleIdeaSelection = (id, ideaIndex) => {
    setSelectedIdeas((prev) => {
      const reportSelections = prev[id] || {};
      const updated = {
        ...prev,
        [id]: {
          ...reportSelections,
          [ideaIndex]: !reportSelections[ideaIndex],
        },
      };
      if (!updated[id][ideaIndex]) {
        delete updated[id][ideaIndex];
      }
      if (Object.keys(updated[id]).length === 0) {
        delete updated[id];
      }
      return updated;
    });
  };

  const getSelectedIdeasForReport = (id) => {
    const selections = selectedIdeas[id] || {};
    return Object.keys(selections)
      .filter((idx) => selections[idx])
      .map((idx) => parseInt(idx, 10));
  };

  const selectAllIdeas = (id, totalIdeas) => {
    const allSelected = {};
    for (let i = 0; i < totalIdeas; i++) {
      allSelected[i] = true;
    }
    setSelectedIdeas((prev) => ({
      ...prev,
      [id]: allSelected,
    }));
  };

  const deselectAllIdeas = (id) => {
    setSelectedIdeas((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleRejectIdea = async (rid, ideaIndex, feedback) => {
    if (!feedback || !feedback.trim()) {
      showToast('Please provide feedback explaining why this angle is being rejected', 'warning');
      return;
    }

    try {
      await researchAPI.rejectIdea(rid, ideaIndex, feedback);
      showToast(
        'Angle rejected. Feedback has been recorded to improve future suggestions.',
        'success'
      );
      setRejectModal(null);
      await reload();
    } catch (err) {
      showToast(
        `Failed to reject angle: ${err.response?.data?.error || err.message || 'Unknown error'}`,
        'error'
      );
      console.error(err);
    }
  };

  const openRejectModal = (rid, ideaIndex, ideaTitle) => {
    setRejectModal({ reportId: rid, ideaIndex, ideaTitle });
  };

  const closeRejectModal = () => setRejectModal(null);

  const pd = report.run_context?.pipeline_debug;

  return (
    <div className="rr-detail-body-inner">
      {pd && (
        <div className="rr-diagnostics rr-diagnostics--detail">
          <button
            type="button"
            className="rr-diagnostics-toggle"
            onClick={() => setDiagnosticsOpen(!diagnosticsOpen)}
            aria-expanded={diagnosticsOpen}
          >
            <span>Pipeline diagnostics</span>
            <span className="rr-diagnostics-chevron">{diagnosticsOpen ? '▼' : '▶'}</span>
          </button>
          {diagnosticsOpen && (
            <div className="rr-diagnostics-body">
              {(!report.article_ideas || report.article_ideas.length === 0) && (
                <p className="rr-diagnostics-hint">
                  No angles were stored for this run. Technical notes below may help explain why.
                </p>
              )}
              {pd.parse_error && (
                <p className="rr-diagnostics-error">
                  <strong>Parse / API:</strong> {pd.parse_error}
                </p>
              )}
              {pd.validation_note && (
                <p className="rr-diagnostics-note">
                  <strong>Validation:</strong> {pd.validation_note}
                </p>
              )}
              {pd.claude_summary && (
                <div className="rr-research-summary-block">
                  <div className="rr-mini-label">Research summary</div>
                  <pre className="rr-research-summary-pre">{pd.claude_summary}</pre>
                </div>
              )}
              {pd.inputs && (
                <dl className="rr-diagnostics-inputs">
                  {Object.entries(pd.inputs).map(([k, v]) => (
                    <div key={k} className="rr-diagnostics-row">
                      <dt>{k}</dt>
                      <dd>{v === undefined || v === null ? '—' : String(v)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}
        </div>
      )}

      {report.article_ideas && report.article_ideas.length > 0 ? (
        <div className="rr-angles">
          <div className="rr-angles-header">
            <h4 className="rr-angles-title">Article angles ({report.article_ideas.length})</h4>
            <div className="rr-bulk-actions">
              <button
                type="button"
                onClick={() => selectAllIdeas(reportId, report.article_ideas.length)}
                className="rr-btn-sm"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => deselectAllIdeas(reportId)}
                className="rr-btn-sm"
              >
                Deselect all
              </button>
            </div>
          </div>
          <div className="rr-angles-table-head" aria-hidden="true">
            <span className="rr-angles-col-topic">Topic</span>
            <span className="rr-angles-col-score">SEO</span>
            <span className="rr-angles-col-score">Lead Value</span>
          </div>
          <ul className="rr-angles-list">
            {report.article_ideas.map((idea, idx) => {
              const isSelected = selectedIdeas[reportId]?.[idx] || false;
              const isApproved = idea.approved || false;
              const isRejected = idea.rejected || false;
              const docFmt = normalizeDocumentFormat(idea.document_format);
              return (
                <li
                  key={idx}
                  className={`rr-angle-item ${isSelected ? 'selected' : ''} ${isApproved ? 'approved' : ''} ${isRejected ? 'rejected' : ''}`}
                >
                  <button
                    type="button"
                    className="rr-angle-menu-btn"
                    title="Angle details"
                    aria-label="Angle details and actions"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAngleDetailModal({
                        reportId,
                        idx,
                        idea,
                      });
                    }}
                  >
                    ⋯
                  </button>
                  <div className="rr-angle-row">
                    <input
                      type="checkbox"
                      id={`idea-${reportId}-${idx}`}
                      checked={isSelected}
                      onChange={() => toggleIdeaSelection(reportId, idx)}
                      disabled={isApproved || isRejected}
                    />
                    <label htmlFor={`idea-${reportId}-${idx}`} className="rr-angle-label">
                      <span className="rr-angle-title">
                        {idea.title || `Angle ${idx + 1}`}
                        {isApproved && <span className="rr-badge rr-badge-approved">Approved</span>}
                        {isRejected && <span className="rr-badge rr-badge-rejected">Rejected</span>}
                      </span>
                      <span className="rr-angle-meta">
                        {idea.target_keyword && (
                          <span className="rr-pill-soft">{idea.target_keyword}</span>
                        )}
                        {docFmt !== 'blog' && (
                          <span className="rr-pill-soft rr-pill-format">
                            {formatLabel(docFmt)}
                          </span>
                        )}
                        {idea.priority != null && (
                          <span className="rr-pill-soft">Priority {idea.priority}/10</span>
                        )}
                      </span>
                    </label>
                    <AngleScoreCell value={idea.seo_value} label="SEO value" />
                    <AngleScoreCell value={idea.lead_value} label="Lead value" />
                  </div>
                  {idea.justification && (
                    <p className="rr-angle-snippet">{idea.justification}</p>
                  )}
                  {idea.rejection_feedback && (
                    <p className="rr-angle-rejection">
                      <strong>Rejection:</strong> {idea.rejection_feedback}
                    </p>
                  )}
                  {!isApproved && !isRejected && (
                    <button
                      type="button"
                      onClick={() =>
                        openRejectModal(reportId, idx, idea.title || `Idea ${idx + 1}`)
                      }
                      className="rr-btn-reject-inline"
                    >
                      Reject…
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="rr-approve-zone">
            {report.profile_id == null && (
              <div className="rr-field-block">
                <label htmlFor={`queue-prof-${reportId}`}>Topic profile for draft queue</label>
                <select
                  id={`queue-prof-${reportId}`}
                  className="rr-select rr-select-block"
                  value={queueProfileChoice[reportId] ?? QUEUE_PROFILE_STANDALONE}
                  onChange={(e) =>
                    setQueueProfileChoice((prev) => ({
                      ...prev,
                      [reportId]: e.target.value,
                    }))
                  }
                >
                  <option value={QUEUE_PROFILE_STANDALONE}>
                    One-off — no saved topic profile
                  </option>
                  {profiles.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              type="button"
              onClick={() =>
                handleApproveIdeas(
                  reportId,
                  getSelectedIdeasForReport(reportId),
                  report
                )
              }
              className="rr-btn-primary"
              disabled={getSelectedIdeasForReport(reportId).length === 0}
            >
              {getSelectedIdeasForReport(reportId).length > 0
                ? `Move ${getSelectedIdeasForReport(reportId).length} selected to draft candidates`
                : 'Select angles to approve'}
            </button>
          </div>
        </div>
      ) : (
        <div className="rr-no-angles">
          <p>No article angles in this report.</p>
        </div>
      )}

      {angleDetailModal && (
        <AngleDetailModal
          reportId={angleDetailModal.reportId}
          idx={angleDetailModal.idx}
          idea={angleDetailModal.idea}
          onClose={() => setAngleDetailModal(null)}
          onRejectRequest={() => {
            const title =
              angleDetailModal.idea.title || `Angle ${angleDetailModal.idx + 1}`;
            openRejectModal(angleDetailModal.reportId, angleDetailModal.idx, title);
            setAngleDetailModal(null);
          }}
        />
      )}

      {rejectModal && (
        <RejectModal
          reportId={rejectModal.reportId}
          ideaIndex={rejectModal.ideaIndex}
          ideaTitle={rejectModal.ideaTitle}
          onReject={handleRejectIdea}
          onClose={closeRejectModal}
        />
      )}
    </div>
  );
}

function AngleDetailModal({ reportId, idx, idea, onClose, onRejectRequest }) {
  const approved = idea.approved || false;
  const rejected = idea.rejected || false;
  const docFmt = normalizeDocumentFormat(idea.document_format);
  return (
    <div className="rr-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="rr-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rr-angle-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rr-modal-head">
          <h4 id="rr-angle-detail-title">Article angle</h4>
          <button type="button" className="rr-modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <p className="rr-angle-detail-muted">Report #{reportId} · angle {idx + 1}</p>
        <h5 className="rr-angle-detail-heading">{idea.title || `Angle ${idx + 1}`}</h5>
        {(idea.target_keyword ||
          docFmt !== 'blog' ||
          idea.priority != null ||
          idea.seo_value != null ||
          idea.lead_value != null) && (
          <div className="rr-angle-detail-tags">
            {idea.target_keyword && <span className="rr-pill-soft">{idea.target_keyword}</span>}
            {docFmt !== 'blog' && (
              <span className="rr-pill-soft rr-pill-format">{formatLabel(docFmt)}</span>
            )}
            {idea.seo_value != null && (
              <span className="rr-pill-soft">SEO {idea.seo_value}/10</span>
            )}
            {idea.lead_value != null && (
              <span className="rr-pill-soft">Lead {idea.lead_value}/10</span>
            )}
            {idea.priority != null && (
              <span className="rr-pill-soft">Priority {idea.priority}/10</span>
            )}
          </div>
        )}
        {idea.justification ? (
          <div className="rr-angle-detail-section">
            <div className="rr-mini-label">Justification</div>
            <p className="rr-angle-detail-body">{idea.justification}</p>
          </div>
        ) : null}
        {idea.rejection_feedback && (
          <div className="rr-angle-detail-section rr-angle-detail-section-warn">
            <div className="rr-mini-label">Rejection note</div>
            <p className="rr-angle-detail-body">{idea.rejection_feedback}</p>
          </div>
        )}
        <div className="rr-modal-actions">
          <button type="button" className="rr-btn-secondary" onClick={onClose}>
            Close
          </button>
          {!approved && !rejected && (
            <button type="button" className="rr-btn-danger" onClick={onRejectRequest}>
              Reject angle…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RejectModal({ reportId, ideaIndex, ideaTitle, onReject, onClose }) {
  const [feedback, setFeedback] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!feedback.trim()) {
      return;
    }
    onReject(reportId, ideaIndex, feedback.trim());
  };

  return (
    <div className="rr-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="rr-modal-panel"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rr-modal-head">
          <h4>Reject article angle</h4>
          <button type="button" className="rr-modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="rr-modal-body">
          <p>
            <strong>Angle:</strong> {ideaTitle}
          </p>
          <p className="rr-modal-hint">Feedback is saved to tune future suggestions.</p>
          <form onSubmit={handleSubmit}>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Why is this angle a poor fit?"
              className="rr-modal-textarea"
              rows={5}
              required
            />
            <div className="rr-modal-actions">
              <button type="button" onClick={onClose} className="rr-btn-secondary">
                Cancel
              </button>
              <button type="submit" className="rr-btn-danger">
                Reject angle
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResearchReportBody;
